import React, { createContext, useState, useEffect, useCallback } from "react";
import { paymentsApi, readStoredPaymentSettings } from "../Components/paymentsApi";
import { isPublicAnonymousRoute } from "../utils/publicAuthPaths";

const DEFAULT_CURRENCY = { currency_code: "LKR", currency_symbol: "Rs." };

function currencyFromStoredSettings() {
  const s = readStoredPaymentSettings();
  if (!s) return null;
  if (s.currency_code == null && s.currency_symbol == null) return null;
  return {
    currency_code: s.currency_code ?? DEFAULT_CURRENCY.currency_code,
    currency_symbol: s.currency_symbol ?? DEFAULT_CURRENCY.currency_symbol,
  };
}

export const CurrencyContext = createContext({
  currencyCode: DEFAULT_CURRENCY.currency_code,
  currencySymbol: DEFAULT_CURRENCY.currency_symbol,
  formatCurrency: (value) => formatCurrencyWithSymbol(value, DEFAULT_CURRENCY.currency_symbol),
  refreshCurrency: () => {},
});

/** Format number with commas and 2 decimal places (e.g. 1234567.5 -> "1,234,567.50") */
export function formatNumberWithCommas(value) {
  if (value == null || value === "" || Number.isNaN(Number(value))) return "0.00";
  return Number(value).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Format value as currency with symbol and commas (e.g. 1234567.5, "Rs." -> "Rs. 1,234,567.50") */
export function formatCurrencyWithSymbol(value, symbol = "Rs.") {
  if (value == null || value === "") return "-";
  const formatted = formatNumberWithCommas(value);
  const sym = (symbol || "Rs.").trim();
  return sym ? `${sym} ${formatted}` : formatted;
}

export function CurrencyProvider({ children }) {
  const [currency, setCurrency] = useState(() => currencyFromStoredSettings() ?? DEFAULT_CURRENCY);
  const [loaded, setLoaded] = useState(false);

  const loadCurrency = useCallback(async (forceRefresh = false) => {
    const path = typeof window !== "undefined" ? window.location.pathname || "/" : "/";
    if (isPublicAnonymousRoute(path)) {
      setLoaded(true);
      return;
    }
    try {
      const data = await paymentsApi.getSettings({ bypassCache: forceRefresh === true });
      if (data && (data.currency_code != null || data.currency_symbol != null)) {
        setCurrency({
          currency_code: data.currency_code ?? DEFAULT_CURRENCY.currency_code,
          currency_symbol: data.currency_symbol ?? DEFAULT_CURRENCY.currency_symbol,
        });
      }
    } catch (_) {
      setCurrency(DEFAULT_CURRENCY);
    } finally {
      setLoaded(true);
    }
  }, []);

  const refreshCurrency = useCallback(() => loadCurrency(true), [loadCurrency]);

  useEffect(() => {
    loadCurrency(false);
  }, [loadCurrency]);

  const formatCurrency = useCallback(
    (value) => {
      if (value == null || value === "") return "-";
      return formatCurrencyWithSymbol(value, currency.currency_symbol);
    },
    [currency.currency_symbol]
  );

  const value = {
    currencyCode: currency.currency_code,
    currencySymbol: currency.currency_symbol,
    formatCurrency,
    refreshCurrency,
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
}
